/**
 * Category filtering across the whole tapahtumat page.
 *
 * Deliberately generic: it toggles .is-filtered-out on EVERY [data-cat] element
 * on the page, so calendar chips, upcoming cards and past cards all filter
 * together without any of them knowing the filter exists.
 *
 * Naming rule this depends on: [data-cat] marks filterable CONTENT; the pills
 * use [data-filter-cat]. A pill carrying data-cat would match its own selector
 * and filter itself out.
 *
 * The pill row ships hidden from PHP and is revealed here, so it is never
 * presented as a control that does nothing when JS is unavailable.
 */

import { applyClamp } from './event-clamp.js';

const CONTENT = '[data-cat]';
const PILL = '[data-filter-cat]';
const GROUP = '[data-event-group]';
const MONTH = '[data-ribbon-month]';

let active = '';

function applyTo(scope = document) {
	scope.querySelectorAll(CONTENT).forEach((node) => {
		const matches = active === '' || node.dataset.cat === active;
		node.classList.toggle('is-filtered-out', !matches);
	});
	recomputeRibbon(scope);
	recomputeGroups(scope);
	// Clamping counts survivors, so it has to run after the filter has decided
	// who they are. One-way: filters -> clamp.
	applyClamp(scope);
}

/**
 * The agenda layout prints a month heading above each group of rows. Filtering
 * every event out of a month would otherwise leave the heading behind,
 * announcing a month with nothing in it. Same idea as recomputeCalendarDays():
 * the events block has no reason to know a filter exists.
 */
function recomputeGroups(scope = document) {
	scope.querySelectorAll(GROUP).forEach((group) => {
		const items = Array.from(group.querySelectorAll(CONTENT));
		if (!items.length) return;
		const surviving = items.some((item) => !item.classList.contains('is-filtered-out'));
		group.classList.toggle('is-filtered-out', !surviving);
	});
}

/**
 * The ribbon draws one bar per event and prints the month total; both are
 * decided at build time, before the filter has a say. Left alone, filtering a
 * category away would leave every month claiming its unfiltered count, and a
 * month with nothing surviving would still look clickable.
 *
 * Recomputed here from survivors, not in the calendar block, which has no
 * reason to know a filter exists.
 */
function recomputeRibbon(scope = document) {
	scope.querySelectorAll(MONTH).forEach((month) => {
		const bars = Array.from(month.querySelectorAll('.rib-bars i'));
		if (!bars.length) return;
		const surviving = bars.filter((bar) => !bar.classList.contains('is-filtered-out'));
		month.classList.toggle('rib--drained', surviving.length === 0);
		const count = month.querySelector('.rib-count');
		if (count) count.textContent = String(surviving.length);
	});
}

/**
 * Show each section's empty state when the filter has hidden everything in it.
 * A day cell in the calendar is not a "section" — only [data-events-section] is.
 */
function updateEmptyStates() {
	document.querySelectorAll('.kpop-cal').forEach((cal) => {
		const empty = cal.querySelector('.cal-empty');
		const months = cal.querySelectorAll(MONTH);
		if (!empty || !months.length) return;
		empty.hidden = [...months].some((m) => !m.classList.contains('rib--drained'));
	});

	document.querySelectorAll('[data-events-section]').forEach((section) => {
		const empty = section.querySelector('.events-empty');
		if (!empty) return;
		const items = section.querySelectorAll(CONTENT);
		if (!items.length) return;
		const visible = Array.from(items).some((n) => !n.classList.contains('is-filtered-out'));
		empty.hidden = visible;
	});
}

function setActive(slug) {
	active = slug;
	applyTo();
	updateEmptyStates();

	document.querySelectorAll(PILL).forEach((pill) => {
		pill.classList.toggle('is-active', pill.dataset.filterCat === active);
		pill.setAttribute('aria-pressed', pill.dataset.filterCat === active ? 'true' : 'false');
	});
}

export function initEventFilters() {
	const pills = document.querySelectorAll(PILL);
	if (!pills.length) return;

	// Reveal the row — it is hidden in PHP so no-JS visitors never see a dead control.
	document.querySelectorAll('.cal-filters').forEach((row) => {
		row.hidden = false;
	});

	pills.forEach((pill) => {
		pill.setAttribute('aria-pressed', pill.classList.contains('is-active') ? 'true' : 'false');
		pill.addEventListener('click', () => setActive(pill.dataset.filterCat || ''));
	});

	// The calendar rebuilds its chips on month navigation; re-apply to the new ones.
	document.addEventListener('kpop:calendar-rendered', (event) => {
		applyTo(event.target);
		updateEmptyStates();
	});
}
